import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Users, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&q=80";

const TeachingStaff = () => {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [desigFilter, setDesigFilter] = useState('All');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    fetch(`${API}/api/cms/teaching-staff`)
      .then(r => r.json())
      .then(d => setStaff(d.staff || []))
      .catch(() => {});
  }, []);

  const departments = useMemo(() => ['All', ...new Set(staff.map(s => s.department))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [staff]);
  const designations = useMemo(() => ['All', ...new Set(staff.map(s => s.designation))], [staff]);

  const filtered = useMemo(() => {
    let result = staff;
    if (deptFilter !== 'All') result = result.filter(s => s.department === deptFilter);
    if (desigFilter !== 'All') result = result.filter(s => s.designation === desigFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.department.toLowerCase().includes(q));
    }
    if (sortCol) {
      result = [...result].sort((a, b) => {
        const va = a[sortCol] || '';
        const vb = b[sortCol] || '';
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return result;
  }, [staff, search, deptFilter, desigFilter, sortCol, sortDir]);

  const deptCounts = useMemo(() => {
    const counts = {};
    staff.forEach(s => { counts[s.department] = (counts[s.department] || 0) + 1; });
    return counts;
  }, [staff]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown size={12} className="text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-[#4ECDC4]" /> : <ChevronDown size={12} className="text-[#4ECDC4]" />;
  };

  return (
    <div className="page-transition" data-testid="teaching-staff-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 hero-overlay" style={{ backgroundImage: `url(${HERO})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">Faculty Directory</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4">Teaching Staff</h1>
            <p className="text-lg text-white/90 leading-relaxed">Complete list of teaching faculty across all departments at SSMCH.</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-6 bg-gradient-to-r from-[#4ECDC4] to-[#3DBDB4]">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8 text-white">
            <div className="text-center"><p className="text-3xl font-bold font-heading">{staff.length}</p><p className="text-white/80 text-sm">Total Faculty</p></div>
            <div className="w-px h-10 bg-white/20 hidden md:block" />
            <div className="text-center"><p className="text-3xl font-bold font-heading">{Object.keys(deptCounts).length}</p><p className="text-white/80 text-sm">Departments</p></div>
            <div className="w-px h-10 bg-white/20 hidden md:block" />
            <div className="text-center"><p className="text-3xl font-bold font-heading">{staff.filter(s => s.designation === 'Professor').length}</p><p className="text-white/80 text-sm">Professors</p></div>
          </div>
        </div>
      </section>

      {/* Filters + Table */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="staff-table-section">
        <div className="container-custom">
          {/* Filters */}
          <Card className="border-0 shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="Search by name or department..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="staff-search" />
                </div>
                <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white min-w-[200px]" data-testid="dept-filter">
                  {departments.map(d => <option key={d} value={d}>{d === 'All' ? 'All Departments' : d} {d !== 'All' && deptCounts[d] ? `(${deptCounts[d]})` : ''}</option>)}
                </select>
                <select value={desigFilter} onChange={e => setDesigFilter(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white min-w-[180px]" data-testid="desig-filter">
                  {designations.map(d => <option key={d} value={d}>{d === 'All' ? 'All Designations' : d}</option>)}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <p className="text-sm text-gray-500 mb-4">{filtered.length} of {staff.length} faculty members</p>

          {/* Table */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="staff-table">
                <thead>
                  <tr className="bg-[#1A202C] text-white">
                    <th className="px-4 py-3 text-left font-medium w-12">S.No</th>
                    <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => toggleSort('name')}>
                      <span className="flex items-center gap-1">Name<SortIcon col="name" /></span>
                    </th>
                    <th className="px-4 py-3 text-left font-medium">Registration</th>
                    <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => toggleSort('department')}>
                      <span className="flex items-center gap-1">Department<SortIcon col="department" /></span>
                    </th>
                    <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => toggleSort('designation')}>
                      <span className="flex items-center gap-1">Designation<SortIcon col="designation" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-[#4ECDC4]/5 transition-colors`} data-testid={`staff-row-${i}`}>
                      <td className="px-4 py-3 text-gray-400 text-xs">{s.sno}</td>
                      <td className="px-4 py-3 font-medium text-[#1A202C]">{s.name}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{s.registration}</td>
                      <td className="px-4 py-3"><span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-2 py-0.5 rounded text-xs font-medium">{s.department}</span></td>
                      <td className="px-4 py-3 text-gray-700">{s.designation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && <div className="p-12 text-center text-gray-400">No faculty members match your search criteria.</div>}
          </Card>
        </div>
      </section>
    </div>
  );
};

export default TeachingStaff;
